import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from './AppHeader';
import { AppFooter } from './AppFooter';
import { TelemetryRouteListener } from '@/app/providers/TelemetryRouteListener';
import { LeerenEntwurfVerwerfenListener } from '@/features/auftragsanlage/components/LeerenEntwurfVerwerfenListener';

/**
 * Top-level layout shell of the portal chrome — organisation-type-agnostic, so the same shell
 * carries Kunde/DELABO-Labor/Support contexts alike (e01-03-ak-1).
 *
 * Vertical stack `flex min-h-screen flex-col`: {@link AppHeader} → `main` (routed content,
 * `flex-1`) → {@link AppFooter}. The **whole document scrolls**; there is deliberately no
 * `position: sticky/fixed` on the footer and no `grid h-screen overflow-hidden` — with short
 * content the footer sits at the bottom of the viewport naturally via `flex-1` on `main`, with
 * long content it scrolls away with the rest of the page (customer-confirmed, rejected the
 * "sticky footer" alternative).
 *
 * Header and footer live outside the `<Outlet />`, so a route change only swaps `main`'s content
 * — header/footer stay mounted across navigation (AK-1). There is intentionally no sidebar here:
 * the Pencil design uses a Top-Nav, not a side nav (rendered inside {@link AppHeader} as
 * `AppTopNav`).
 *
 * `main`'s content is wrapped in a `mx-auto max-w-[1440px]` column, matching every Pencil frame's
 * fixed 1440px width — {@link AppHeader} and `AppFooter` apply the same max-width to their own
 * inner rows so the whole shell lines up on one centered column. This wrapper carries the
 * shell-wide `pt-8` (32px) — the single source of truth for the header-to-content gap — and the
 * shell-wide `pb-10` (40px) — the single source of truth for the content-to-footer gap, so every
 * routed page's headline/content sits at the same distance from {@link AppHeader}/`AppFooter`
 * without each route managing its own top/bottom spacing. Routes still pad themselves
 * horizontally (`px-6`, e.g. `ProfileRoute`, `DashboardRoute`, …) — routes must **not** add their
 * own bottom padding (`pb-*`), or the gap would double up on top of this wrapper's `pb-10`.
 */
export function AppShell() {
  const { t } = useTranslation('app');
  return (
    <div className="flex min-h-screen flex-col">
      <TelemetryRouteListener />
      <LeerenEntwurfVerwerfenListener />
      <AppHeader />
      <main
        className="flex-1"
        id="main-content"
        tabIndex={-1}
        aria-label={t('layout.mainContent')}
      >
        <div className="mx-auto max-w-[1440px] pt-8 pb-10">
          <Outlet />
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
